import React, { useState, useEffect, useMemo } from 'react';
import {
  Plus, Pencil, Trash2, Search, MoreVertical, User, UserPlus,
  ShieldCheck, Shield, CheckCircle2, Circle, Phone, Globe, Clock,
  BadgeCheck, Ban, Send, RefreshCw, ChevronLeft, ChevronRight,
  Loader2, X, ChevronsLeft, ChevronsRight,
} from 'lucide-react';
import api from '../services/api';
import { customRolesAPI } from '../services/api';
import AvatarUpload from '../components/Avatar/AvatarUpload';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import {
  Card, CardContent, CardFooter, CardHeader, CardTitle,
  CardDescription, CardAction,
} from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from '../components/ui/use-toast';
import {
  Sheet, SheetContent, SheetTitle,
} from '../components/ui/sheet';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ToggleGroup, ToggleGroupItem } from '../components/ui/toggle-group';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../components/ui/tooltip';

// ── Interfaces ────────────────────────────────────────────────────────────────

interface Agent {
  id: number;
  account_id: number;
  email: string;
  first_name: string;
  last_name: string;
  display_name?: string;
  full_name: string;
  initials: string;
  avatar_url?: string;
  role: 'agent' | 'admin' | 'super_admin';
  status: 'active' | 'inactive' | 'suspended';
  availability_status: 'available' | 'busy' | 'offline';
  auto_offline: boolean;
  confirmed: boolean;
  is_verified: boolean;
  phone_number?: string;
  timezone: string;
  language: string;
  custom_role_id?: number;
  created_at: string;
  updated_at: string;
  name: string;
  available_name: string;
  thumbnail?: string;
}

interface AgentStats {
  total_agents: number;
  active_agents: number;
  available_agents: number;
  busy_agents: number;
  offline_agents: number;
  confirmed_agents: number;
  unconfirmed_agents: number;
}

type FormData = {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  display_name: string;
  phone_number: string;
  role: string;
  custom_role_id: number | null;
  timezone: string;
  language: string;
  availability_status: string;
  auto_offline: boolean;
  confirmed: boolean;
  avatar_url: string;
};

// ── Config lookups ────────────────────────────────────────────────────────────

const AVAILABILITY_CONFIG: Record<string, { dot: string; badge: string; label: string }> = {
  available: {
    dot: 'bg-emerald-500',
    badge: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-400',
    label: 'Available',
  },
  busy: {
    dot: 'bg-amber-500',
    badge: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-400',
    label: 'Busy',
  },
  offline: {
    dot: 'bg-zinc-400',
    badge: '',
    label: 'Offline',
  },
};

const ROLE_CONFIG: Record<string, { label: string; Icon: React.ElementType; iconClass: string }> = {
  super_admin: { label: 'Super Admin', Icon: ShieldCheck, iconClass: 'text-primary' },
  admin:       { label: 'Admin',       Icon: Shield,      iconClass: 'text-blue-600 dark:text-blue-400' },
  agent:       { label: 'Agent',       Icon: User,        iconClass: 'text-muted-foreground' },
};

const STATUS_BADGE: Record<string, string> = {
  active:    'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-400',
  inactive:  'border-zinc-200 bg-zinc-50 text-zinc-600 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400',
  suspended: 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400',
};

const DEFAULT_FORM: FormData = {
  first_name: '', last_name: '', email: '', password: '',
  display_name: '', phone_number: '', role: 'agent',
  custom_role_id: null, timezone: 'UTC', language: 'en',
  availability_status: 'available', auto_offline: true,
  confirmed: true, avatar_url: '',
};

// ── Sub-components ────────────────────────────────────────────────────────────

const AgentAvatarWithStatus: React.FC<{ agent: Agent; size?: 'sm' | 'md' }> = ({ agent, size = 'md' }) => {
  const cfg = AVAILABILITY_CONFIG[agent.availability_status] ?? AVAILABILITY_CONFIG.offline;
  return (
    <div className="relative shrink-0">
      <Avatar className={size === 'sm' ? 'h-9 w-9' : 'h-10 w-10'}>
        <AvatarImage src={agent.avatar_url} />
        <AvatarFallback className="text-xs">{agent.initials}</AvatarFallback>
      </Avatar>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={cn(
              'absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-background cursor-default',
              cfg.dot,
            )} />
          </TooltipTrigger>
          <TooltipContent side="right" className="capitalize">{cfg.label}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};

interface AgentContextMenuProps {
  agent: Agent;
  onEdit: () => void;
  onDelete: () => void;
  onStatusUpdate: (status: string) => void;
  onResendInvite: () => void;
}

const AgentContextMenu: React.FC<AgentContextMenuProps> = ({
  agent, onEdit, onDelete, onStatusUpdate, onResendInvite,
}) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
        <MoreVertical className="h-4 w-4" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-48">
      <DropdownMenuItem onClick={onEdit}>
        <Pencil className="h-4 w-4 mr-2" />Edit Agent
      </DropdownMenuItem>
      {!agent.confirmed && (
        <DropdownMenuItem onClick={onResendInvite}>
          <Send className="h-4 w-4 mr-2" />Resend Invitation
        </DropdownMenuItem>
      )}
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={() => onStatusUpdate('available')}>
        <CheckCircle2 className="h-4 w-4 mr-2 text-emerald-500" />Set Available
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => onStatusUpdate('busy')}>
        <Circle className="h-4 w-4 mr-2 text-amber-500" />Set Busy
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => onStatusUpdate('offline')}>
        <Circle className="h-4 w-4 mr-2 text-zinc-400" />Set Offline
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={onDelete}>
        <Trash2 className="h-4 w-4 mr-2" />Remove Agent
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

// ── Form tabs ─────────────────────────────────────────────────────────────────

const ProfileTab: React.FC<{
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  isEdit: boolean;
}> = ({ formData, setFormData, isEdit }) => (
  <div className="space-y-4">
    <div className="flex justify-center mb-2">
      <AvatarUpload
        currentAvatar={formData.avatar_url}
        onAvatarChange={(url) => setFormData((p) => ({ ...p, avatar_url: url || '' }))}
        name={`${formData.first_name} ${formData.last_name}`.trim()}
        size={96}
      />
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor="first-name">First Name *</Label>
        <Input id="first-name" value={formData.first_name}
          onChange={(e) => setFormData((p) => ({ ...p, first_name: e.target.value }))}
          placeholder="First name" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="last-name">Last Name *</Label>
        <Input id="last-name" value={formData.last_name}
          onChange={(e) => setFormData((p) => ({ ...p, last_name: e.target.value }))}
          placeholder="Last name" />
      </div>
    </div>
    {!isEdit && (
      <>
        <div className="space-y-2">
          <Label htmlFor="email">Email *</Label>
          <Input id="email" type="email" value={formData.email}
            onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
            placeholder="email@example.com" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password *</Label>
          <Input id="password" type="password" value={formData.password}
            onChange={(e) => setFormData((p) => ({ ...p, password: e.target.value }))}
            placeholder="••••••••" />
        </div>
      </>
    )}
    <div className="space-y-2">
      <Label htmlFor="display-name">Display Name (Optional)</Label>
      <Input id="display-name" value={formData.display_name}
        onChange={(e) => setFormData((p) => ({ ...p, display_name: e.target.value }))}
        placeholder="Display name" />
    </div>
  </div>
);

const SettingsTab: React.FC<{
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
}> = ({ formData, setFormData }) => (
  <div className="space-y-4">
    <div className="space-y-2">
      <Label htmlFor="phone-number">Phone Number (Optional)</Label>
      <Input id="phone-number" value={formData.phone_number}
        onChange={(e) => setFormData((p) => ({ ...p, phone_number: e.target.value }))}
        placeholder="+1234567890" />
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label>Timezone</Label>
        <Select value={formData.timezone} onValueChange={(v) => setFormData((p) => ({ ...p, timezone: v }))}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="UTC">UTC</SelectItem>
            <SelectItem value="America/New_York">Eastern Time</SelectItem>
            <SelectItem value="America/Chicago">Central Time</SelectItem>
            <SelectItem value="America/Denver">Mountain Time</SelectItem>
            <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Language</Label>
        <Select value={formData.language} onValueChange={(v) => setFormData((p) => ({ ...p, language: v }))}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="en">English</SelectItem>
            <SelectItem value="es">Spanish</SelectItem>
            <SelectItem value="fr">French</SelectItem>
            <SelectItem value="de">German</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
    <div className="flex items-center gap-3 pt-1">
      <Switch id="auto-offline" checked={formData.auto_offline}
        onCheckedChange={(v) => setFormData((p) => ({ ...p, auto_offline: v }))} />
      <Label htmlFor="auto-offline" className="cursor-pointer">Auto Offline</Label>
    </div>
  </div>
);

const PermissionsTab: React.FC<{
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  customRoles: any[];
  isEdit: boolean;
}> = ({ formData, setFormData, customRoles, isEdit }) => (
  <div className="space-y-4">
    <div className="space-y-2">
      <Label>Role</Label>
      <Select value={formData.role} onValueChange={(v) => setFormData((p) => ({ ...p, role: v }))}>
        <SelectTrigger><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="agent">Agent</SelectItem>
          <SelectItem value="admin">Admin</SelectItem>
          <SelectItem value="super_admin">Super Admin</SelectItem>
        </SelectContent>
      </Select>
    </div>
    <div className="space-y-2">
      <Label>Custom Role (Optional)</Label>
      <Select
        value={formData.custom_role_id ? String(formData.custom_role_id) : 'none'}
        onValueChange={(v) => setFormData((p) => ({ ...p, custom_role_id: v === 'none' ? null : Number(v) }))}
      >
        <SelectTrigger><SelectValue placeholder="None — Use Base Role" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none">None — Use Base Role</SelectItem>
          {customRoles.map((role) => (
            <SelectItem key={role.id} value={String(role.id)}>
              {role.name} ({role.permission_count} permissions)
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
    {isEdit && (
      <div className="space-y-2">
        <Label>Availability</Label>
        <Select value={formData.availability_status}
          onValueChange={(v) => setFormData((p) => ({ ...p, availability_status: v }))}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="available">Available</SelectItem>
            <SelectItem value="busy">Busy</SelectItem>
            <SelectItem value="offline">Offline</SelectItem>
          </SelectContent>
        </Select>
      </div>
    )}
  </div>
);

// ── Skeleton loading ──────────────────────────────────────────────────────────

const AgentCardSkeleton: React.FC = () => (
  <Card className="flex flex-col">
    <CardHeader className="pb-2">
      <div className="flex items-center gap-3">
        <Skeleton className="h-10 w-10 rounded-full shrink-0" />
        <div className="space-y-1.5 flex-1">
          <Skeleton className="h-3.5 w-28" />
          <Skeleton className="h-3 w-36" />
        </div>
      </div>
    </CardHeader>
    <CardContent className="pb-2">
      <div className="flex gap-1.5">
        <Skeleton className="h-5 w-16 rounded-full" />
        <Skeleton className="h-5 w-12 rounded-full" />
      </div>
    </CardContent>
    <CardFooter className="pt-2">
      <Skeleton className="h-8 w-full" />
    </CardFooter>
  </Card>
);

// ── Main component ────────────────────────────────────────────────────────────

const AgentsPage: React.FC = () => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [stats, setStats] = useState<AgentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customRoles, setCustomRoles] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [agentToDelete, setAgentToDelete] = useState<Agent | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [availabilityFilter, setAvailabilityFilter] = useState('');

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const allSelected = agents.length > 0 && agents.every((a) => selectedIds.has(a.id));
  const someSelected = selectedIds.size > 0 && !allSelected;
  const toggleRow = (id: number) =>
    setSelectedIds((prev) => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const toggleAll = () =>
    setSelectedIds(allSelected ? new Set() : new Set(agents.map((a) => a.id)));

  const [formData, setFormData] = useState<FormData>(DEFAULT_FORM);

  useEffect(() => {
    fetchData();
    fetchCustomRoles();
  }, [page, perPage, searchQuery, roleFilter, statusFilter, availabilityFilter]);

  useEffect(() => {
    if (total <= 0) return;
    if (page > totalPages) setPage(totalPages);
  }, [total, totalPages, page]);

  const safeTotalPages = useMemo(() => Math.max(1, totalPages || 1), [totalPages]);

  const goToPage = (p: number) => {
    setPage(Math.max(1, Math.min(p, safeTotalPages)));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const fetchCustomRoles = async () => {
    try {
      const rolesData = await customRolesAPI.getRoles();
      setCustomRoles(rolesData.filter((r: any) => r.is_active));
    } catch {
      // silently fail
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: page.toString(), per_page: perPage.toString() });
      if (searchQuery) params.append('search', searchQuery);
      if (roleFilter) params.append('role', roleFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (availabilityFilter) params.append('availability_status', availabilityFilter);

      const [agentsRes, statsRes] = await Promise.all([
        api.get(`/agents/?${params.toString()}`),
        api.get('/agents/stats'),
      ]);
      setAgents(agentsRes.data.agents || []);
      const totalVal = agentsRes.data.total || 0;
      setTotal(totalVal);
      const rawPages = agentsRes.data.total_pages ?? 0;
      setTotalPages(totalVal === 0 ? 1 : Math.max(1, rawPages));
      setStats(statsRes.data);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAgent = async () => {
    try {
      setSaving(true);
      await api.post('/agents/', formData);
      setIsSheetOpen(false);
      resetForm();
      fetchData();
      toast({ title: 'Agent added', description: 'Agent created successfully' });
    } catch (error: any) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to create agent', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateAgent = async () => {
    if (!selectedAgent) return;
    try {
      setSaving(true);
      await api.patch(`/agents/${selectedAgent.id}`, formData);
      setIsSheetOpen(false);
      setSelectedAgent(null);
      resetForm();
      fetchData();
      toast({ title: 'Agent updated', description: 'Agent updated successfully' });
    } catch (error: any) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to update agent', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAgent = async (agent: Agent) => {
    try {
      await api.delete(`/agents/${agent.id}`);
      fetchData();
      toast({ title: 'Agent removed', description: 'Agent removed successfully' });
    } catch (error: any) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to remove agent', variant: 'destructive' });
    } finally {
      setDeleteConfirmOpen(false);
      setAgentToDelete(null);
    }
  };

  const handleStatusUpdate = async (agentId: number, availability_status: string) => {
    // Optimistic update
    setAgents((prev) =>
      prev.map((a) =>
        a.id === agentId
          ? { ...a, availability_status: availability_status as Agent['availability_status'] }
          : a,
      ),
    );
    try {
      await api.patch(`/agents/${agentId}/status`, { availability_status });
    } catch {
      fetchData();
      toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' });
    }
  };

  const handleResendInvitation = async (agentId: number) => {
    try {
      await api.post(`/agents/${agentId}/invite`);
      toast({ title: 'Invitation sent', description: 'Invitation email resent successfully' });
    } catch {
      toast({ title: 'Error', description: 'Failed to resend invitation', variant: 'destructive' });
    }
  };

  const handleBulkStatusUpdate = async (availability_status: string) => {
    const ids = Array.from(selectedIds);
    setAgents((prev) =>
      prev.map((a) =>
        selectedIds.has(a.id) ? { ...a, availability_status: availability_status as Agent['availability_status'] } : a,
      ),
    );
    try {
      await Promise.all(ids.map((id) => api.patch(`/agents/${id}/status`, { availability_status })));
      setSelectedIds(new Set());
      toast({ title: 'Status updated', description: `Updated ${ids.length} agent${ids.length !== 1 ? 's' : ''}` });
    } catch {
      fetchData();
      toast({ title: 'Error', description: 'Some updates failed', variant: 'destructive' });
    }
  };

  const resetForm = () => setFormData(DEFAULT_FORM);

  const openSheet = (agent?: Agent) => {
    if (agent) {
      setSelectedAgent(agent);
      setFormData({
        first_name: agent.first_name,
        last_name: agent.last_name,
        email: agent.email,
        password: '',
        display_name: agent.display_name || '',
        phone_number: agent.phone_number || '',
        role: agent.role,
        custom_role_id: agent.custom_role_id || null,
        timezone: agent.timezone,
        language: agent.language,
        availability_status: agent.availability_status,
        auto_offline: agent.auto_offline,
        confirmed: agent.confirmed,
        avatar_url: agent.avatar_url || '',
      });
    } else {
      setSelectedAgent(null);
      resetForm();
    }
    setIsSheetOpen(true);
  };

  const closeSheet = () => {
    setIsSheetOpen(false);
    setSelectedAgent(null);
    resetForm();
  };

  // ── Computed values ───────────────────────────────────────────────────────
  const safeDenominator = stats?.total_agents || 1;
  const availPct = stats ? (stats.available_agents / safeDenominator) * 100 : 0;
  const busyPct  = stats ? (stats.busy_agents  / safeDenominator) * 100 : 0;
  const offlinePct = stats ? (stats.offline_agents / safeDenominator) * 100 : 0;

  const activeFilters: Array<{ key: string; label: string; clear: () => void }> = [
    ...(searchQuery      ? [{ key: 'search',       label: `Search: "${searchQuery}"`, clear: () => { setSearchQuery(''); setPage(1); } }] : []),
    ...(roleFilter       ? [{ key: 'role',          label: `Role: ${ROLE_CONFIG[roleFilter]?.label ?? roleFilter}`, clear: () => { setRoleFilter(''); setPage(1); } }] : []),
    ...(statusFilter     ? [{ key: 'status',        label: `Status: ${statusFilter}`,  clear: () => { setStatusFilter(''); setPage(1); } }] : []),
    ...(availabilityFilter ? [{ key: 'avail',       label: `Availability: ${AVAILABILITY_CONFIG[availabilityFilter]?.label ?? availabilityFilter}`, clear: () => { setAvailabilityFilter(''); setPage(1); } }] : []),
  ];

  const { rangeStart, rangeEnd } = useMemo(() => {
    if (total <= 0) return { rangeStart: 0, rangeEnd: 0 };
    return {
      rangeStart: (page - 1) * perPage + 1,
      rangeEnd: Math.min(page * perPage, total),
    };
  }, [total, page, perPage]);

  const isCreate  = !selectedAgent;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-1 flex-col gap-4 py-4 px-4 md:gap-6 md:py-6 lg:px-6">

      {/* ── Page header ──────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Agents</h1>
          <p className="text-sm text-muted-foreground">
            Manage your support team{total > 0 ? ` · ${total} total` : ''}
          </p>
        </div>
        <Button onClick={() => openSheet()}>
          <UserPlus className="h-4 w-4 mr-2" />
          Invite Agent
        </Button>
      </div>

      {/* ── KPI cards ────────────────────────────────────────────────────── */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm @container/card">
          <CardHeader>
            <CardDescription>Total Agents</CardDescription>
            <CardTitle className="text-2xl font-semibold tabular-nums">
              {stats?.total_agents ?? 0}
            </CardTitle>
            <CardAction>
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                <User className="h-5 w-5 text-primary" />
              </div>
            </CardAction>
          </CardHeader>
          <CardFooter className="flex-col items-start gap-1 pt-0">
            <Progress value={stats ? (stats.active_agents / safeDenominator) * 100 : 0} className="h-1.5 w-full" />
            <p className="text-xs text-muted-foreground">
              {stats?.active_agents ?? 0} active
            </p>
          </CardFooter>
        </Card>

        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm @container/card">
          <CardHeader>
            <CardDescription>Available</CardDescription>
            <CardTitle className="text-2xl font-semibold tabular-nums">
              {stats?.available_agents ?? 0}
            </CardTitle>
            <CardAction>
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-950">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              </div>
            </CardAction>
          </CardHeader>
          <CardFooter className="flex-col items-start gap-1 pt-0">
            <Progress value={availPct} className="h-1.5 w-full [&>div]:bg-emerald-500" />
            <p className="text-xs text-muted-foreground">
              {Math.round(availPct)}% of team
            </p>
          </CardFooter>
        </Card>

        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm @container/card">
          <CardHeader>
            <CardDescription>Busy</CardDescription>
            <CardTitle className="text-2xl font-semibold tabular-nums">
              {stats?.busy_agents ?? 0}
            </CardTitle>
            <CardAction>
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-950">
                <Circle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
            </CardAction>
          </CardHeader>
          <CardFooter className="flex-col items-start gap-1 pt-0">
            <Progress value={busyPct} className="h-1.5 w-full [&>div]:bg-amber-500" />
            <p className="text-xs text-muted-foreground">
              {Math.round(busyPct)}% of team
            </p>
          </CardFooter>
        </Card>

        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm @container/card">
          <CardHeader>
            <CardDescription>Offline</CardDescription>
            <CardTitle className="text-2xl font-semibold tabular-nums">
              {stats?.offline_agents ?? 0}
            </CardTitle>
            <CardAction>
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                <Ban className="h-5 w-5 text-muted-foreground" />
              </div>
            </CardAction>
          </CardHeader>
          <CardFooter className="flex-col items-start gap-1 pt-0">
            <Progress value={offlinePct} className="h-1.5 w-full [&>div]:bg-zinc-400" />
            <p className="text-xs text-muted-foreground">
              {Math.round(offlinePct)}% of team
            </p>
          </CardFooter>
        </Card>
      </div>

      {/* ── Availability overview bar ─────────────────────────────────────── */}
      {stats && (
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Team Availability</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="bg-emerald-500 transition-all duration-500"
                style={{ width: `${availPct}%` }}
              />
              <div
                className="bg-amber-500 transition-all duration-500"
                style={{ width: `${busyPct}%` }}
              />
              <div
                className="bg-zinc-400 transition-all duration-500"
                style={{ width: `${offlinePct}%` }}
              />
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-emerald-500 shrink-0" />
                Available ({stats.available_agents})
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-amber-500 shrink-0" />
                Busy ({stats.busy_agents})
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-zinc-400 shrink-0" />
                Offline ({stats.offline_agents})
              </span>
              <span className="ml-auto">
                {stats.confirmed_agents} confirmed · {stats.unconfirmed_agents} pending invite
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Filter toolbar ────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search agents..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
            className="pl-9 h-9"
          />
        </div>

        <Select value={roleFilter || 'all'} onValueChange={(v) => { setRoleFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-[130px] h-9"><SelectValue placeholder="Role" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="agent">Agent</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="super_admin">Super Admin</SelectItem>
          </SelectContent>
        </Select>

        <Select value={statusFilter || 'all'} onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-[130px] h-9"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
          </SelectContent>
        </Select>

        <Select value={availabilityFilter || 'all'} onValueChange={(v) => { setAvailabilityFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-[130px] h-9"><SelectValue placeholder="Availability" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="available">Available</SelectItem>
            <SelectItem value="busy">Busy</SelectItem>
            <SelectItem value="offline">Offline</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex items-center gap-1 ml-auto">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9" onClick={fetchData}>
                  <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Refresh</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* ── Active filter chips ───────────────────────────────────────────── */}
      {activeFilters.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {activeFilters.map((f) => (
            <Badge key={f.key} variant="secondary" className="gap-1 pr-1 text-xs font-normal">
              {f.label}
              <button
                onClick={f.clear}
                className="ml-0.5 rounded-sm opacity-60 hover:opacity-100 transition-opacity"
                aria-label={`Remove ${f.label} filter`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          <button
            onClick={() => { setSearchQuery(''); setRoleFilter(''); setStatusFilter(''); setAvailabilityFilter(''); setPage(1); }}
            className="text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline transition-colors"
          >
            Clear all
          </button>
        </div>
      )}

      {/* ── Content ──────────────────────────────────────────────────────────── */}
      {loading ? (
        <Card>
          <CardContent className="p-0">
            <div className="max-h-[min(70vh,40rem)] overflow-y-auto overscroll-y-contain border-b border-border [&>div]:overflow-visible">
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm shadow-[inset_0_-1px_0_0_hsl(var(--border))]">
                <TableRow>
                  <TableHead className="w-10 pl-4" />
                  <TableHead>Agent</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Availability</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell className="pl-4"><Skeleton className="h-4 w-4 rounded" /></TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-9 w-9 rounded-full shrink-0" />
                        <div className="space-y-1.5">
                          <Skeleton className="h-3.5 w-28" />
                          <Skeleton className="h-3 w-36" />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell><Skeleton className="h-5 w-16 rounded-full" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-14 rounded-full" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-20 rounded-full" /></TableCell>
                    <TableCell><Skeleton className="h-3.5 w-24" /></TableCell>
                    <TableCell><Skeleton className="h-8 w-8 rounded" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          </CardContent>
        </Card>
      ) : agents.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
            <User className="h-8 w-8 text-muted-foreground" />
          </div>
          <p className="text-sm font-medium">No agents found</p>
          <p className="text-xs text-muted-foreground mt-1 max-w-xs">
            {activeFilters.length > 0
              ? 'Try adjusting or clearing your filters to see results.'
              : 'Invite your first agent to get started.'}
          </p>
          {activeFilters.length === 0 && (
            <Button size="sm" className="mt-4" onClick={() => openSheet()}>
              <UserPlus className="h-4 w-4 mr-2" />
              Invite Agent
            </Button>
          )}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0 pb-2">
            <div className="max-h-[min(70vh,40rem)] overflow-y-auto overscroll-y-contain border-b border-border [&>div]:overflow-visible">
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm shadow-[inset_0_-1px_0_0_hsl(var(--border))]">
                <TableRow>
                  <TableHead className="w-10 pl-4">
                    <Checkbox
                      checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                      onCheckedChange={toggleAll}
                      aria-label="Select all agents"
                    />
                  </TableHead>
                  <TableHead>Agent</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Availability</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {agents.map((agent) => {
                  const avCfg   = AVAILABILITY_CONFIG[agent.availability_status] ?? AVAILABILITY_CONFIG.offline;
                  const roleCfg = ROLE_CONFIG[agent.role] ?? ROLE_CONFIG.agent;
                  const RoleIcon = roleCfg.Icon;
                  const isSelected = selectedIds.has(agent.id);
                  return (
                    <TableRow
                      key={agent.id}
                      className={cn('group', isSelected && 'bg-accent/50')}
                      data-state={isSelected ? 'selected' : undefined}
                    >
                      <TableCell className="pl-4">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleRow(agent.id)}
                          aria-label={`Select ${agent.available_name}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <AgentAvatarWithStatus agent={agent} size="sm" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium flex items-center gap-1">
                              <span className="truncate">{agent.available_name}</span>
                              {agent.is_verified && <BadgeCheck className="h-3.5 w-3.5 text-primary shrink-0" />}
                              {!agent.confirmed && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Ban className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                                    </TooltipTrigger>
                                    <TooltipContent>Invitation pending</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </p>
                            <p className="text-xs text-muted-foreground">{agent.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs gap-1">
                          <RoleIcon className={cn('h-3 w-3', roleCfg.iconClass)} />
                          {roleCfg.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={cn('text-xs', STATUS_BADGE[agent.status] ?? '')}>
                          {agent.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={cn('text-xs', avCfg.badge)}>
                          ● {avCfg.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {agent.phone_number && (
                          <p className="flex items-center gap-1">
                            <Phone className="h-3 w-3 shrink-0" />{agent.phone_number}
                          </p>
                        )}
                        <p className="flex items-center gap-1">
                          <Globe className="h-3 w-3 shrink-0" />{agent.language.toUpperCase()}
                          <span className="mx-0.5">·</span>
                          <Clock className="h-3 w-3 shrink-0" />{agent.timezone}
                        </p>
                      </TableCell>
                      <TableCell>
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex justify-end">
                          <AgentContextMenu
                            agent={agent}
                            onEdit={() => openSheet(agent)}
                            onDelete={() => { setAgentToDelete(agent); setDeleteConfirmOpen(true); }}
                            onStatusUpdate={(s) => handleStatusUpdate(agent.id, s)}
                            onResendInvite={() => handleResendInvitation(agent.id)}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </div>

            {!loading && total > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-border shrink-0">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="hidden sm:inline">Rows per page</span>
                  <Select
                    value={String(perPage)}
                    onValueChange={(v) => {
                      setPerPage(Number(v));
                      setPage(1);
                    }}
                  >
                    <SelectTrigger className="h-7 w-[72px] text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5</SelectItem>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="tabular-nums">
                    {total === 0
                      ? '0 of 0'
                      : `${rangeStart}–${rangeEnd} of ${total.toLocaleString()}`}
                  </span>
                  <div className="flex items-center gap-0.5">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => goToPage(1)}
                      disabled={page <= 1}
                      aria-label="First page"
                    >
                      <ChevronsLeft className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => goToPage(page - 1)}
                      disabled={page <= 1}
                      aria-label="Previous page"
                    >
                      <ChevronLeft className="h-3.5 w-3.5" />
                    </Button>
                    <span className="min-w-[5rem] text-center text-xs tabular-nums">
                      Page {page} of {safeTotalPages}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => goToPage(page + 1)}
                      disabled={page >= safeTotalPages}
                      aria-label="Next page"
                    >
                      <ChevronRight className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => goToPage(safeTotalPages)}
                      disabled={page >= safeTotalPages}
                      aria-label="Last page"
                    >
                      <ChevronsRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Bulk action floating bar ──────────────────────────────────────── */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 flex items-center gap-2 rounded-xl bg-foreground text-background px-4 py-2.5 shadow-2xl z-50 animate-in slide-in-from-bottom-4 duration-200">
          <span className="text-sm font-medium mr-1 whitespace-nowrap">
            {selectedIds.size} selected
          </span>
          <Separator orientation="vertical" className="h-4 bg-background/20 shrink-0" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="secondary" className="h-7 text-xs gap-1">
                Set Status
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" side="top">
              <DropdownMenuItem onClick={() => handleBulkStatusUpdate('available')}>
                <CheckCircle2 className="h-4 w-4 mr-2 text-emerald-500" />Set Available
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleBulkStatusUpdate('busy')}>
                <Circle className="h-4 w-4 mr-2 text-amber-500" />Set Busy
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleBulkStatusUpdate('offline')}>
                <Circle className="h-4 w-4 mr-2 text-zinc-400" />Set Offline
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Separator orientation="vertical" className="h-4 bg-background/20 shrink-0" />
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 hover:bg-background/10 text-background hover:text-background"
            onClick={() => setSelectedIds(new Set())}
            aria-label="Clear selection"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}

      {/* ── Mobile FAB (shifted left to avoid Customizer button) ──────────── */}
      <div className="fixed bottom-6 right-20 sm:hidden z-40">
        <Button
          size="icon"
          className="h-14 w-14 rounded-full shadow-lg"
          onClick={() => openSheet()}
        >
          <Plus className="h-6 w-6" />
        </Button>
      </div>

      {/* ── Agent Sheet (Create / Edit) ───────────────────────────────────── */}
      <Sheet open={isSheetOpen} onOpenChange={(open) => { if (!open) closeSheet(); }}>
        <SheetContent
          side="right"
          className="sm:max-w-lg p-0 flex flex-col [&>button]:hidden"
        >
          {/* Sticky header */}
          <div className="flex items-center justify-between border-b px-4 py-3 shrink-0">
            <SheetTitle className="text-sm font-semibold">
              {isCreate ? 'Invite Agent' : `Edit Agent · ${selectedAgent?.available_name}`}
            </SheetTitle>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={closeSheet}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>

          {/* Tabbed form */}
          <Tabs defaultValue="profile" className="flex flex-1 flex-col min-h-0">
            <div className="px-4 pt-3 pb-3 shrink-0">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="profile" className="text-xs">Profile</TabsTrigger>
                <TabsTrigger value="settings" className="text-xs">Settings</TabsTrigger>
                <TabsTrigger value="permissions" className="text-xs">Permissions</TabsTrigger>
              </TabsList>
            </div>
            <ScrollArea className="flex-1">
              <TabsContent value="profile" className="mt-0 p-4 space-y-4">
                <ProfileTab formData={formData} setFormData={setFormData} isEdit={!isCreate} />
              </TabsContent>
              <TabsContent value="settings" className="mt-0 p-4 space-y-4">
                <SettingsTab formData={formData} setFormData={setFormData} />
              </TabsContent>
              <TabsContent value="permissions" className="mt-0 p-4 space-y-4">
                <PermissionsTab
                  formData={formData}
                  setFormData={setFormData}
                  customRoles={customRoles}
                  isEdit={!isCreate}
                />
              </TabsContent>
            </ScrollArea>
          </Tabs>

          {/* Sticky footer */}
          <div className="border-t p-4 flex justify-end gap-2 shrink-0">
            <Button variant="outline" onClick={closeSheet} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={isCreate ? handleCreateAgent : handleUpdateAgent}
              disabled={saving}
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isCreate ? 'Invite Agent' : 'Save Changes'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* ── Delete confirmation ───────────────────────────────────────────── */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Agent</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove{' '}
              <span className="font-medium text-foreground">{agentToDelete?.available_name}</span>?{' '}
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setDeleteConfirmOpen(false); setAgentToDelete(null); }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => agentToDelete && handleDeleteAgent(agentToDelete)}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AgentsPage;
